<template>
  <FlotoDropdownPicker
    :multiple="multiple"
    :use-mselect="false"
    :options="options"
    :as-input="asInputComputed"
    :value="value"
    :keep-unassigned="keepUnassigned"
    :disabled="disabled"
    searchable
    is-larg-data-set
    :placeholder="placeholder"
    :overlay-style="{ zIndex: 1051 }"
    :focus-event-brodcast="focusEventBrodcast"
    :advanced-searchable="advancedSearchable && !isPortalLogin"
    overlay-class-name="picker-overlay wide-picker-overlay"
    v-bind="$attrs"
    @change="handleChange"
    @selected="handleSelected"
  >
    <template v-if="!multiple" v-slot:trigger="triggerSlotData">
      <slot name="trigger" v-bind="triggerSlotData">
        <template v-if="!asInputComputed">
          <span
            v-if="triggerSlotData.currentItem"
            ref="triggerRef"
            :tabindex="disabled ? -1 : 0"
            :role="disabled ? undefined : 'button'"
            class="flex items-center technician-picker-trigger dropdown-pill-trigger"
            :class="{
              'cursor-pointer': !disabled,
              'cursor-not-allowed': disabled,
              'dropdown-pill-trigger--disabled': disabled,
            }"
            @click="triggerSlotData.toggle"
            @keydown.space.prevent.enter.stop="
              ($event) => handleKeyDown($event, 'triggerRef')
            "
          >
            <slot
              name="selected-technician"
              :technician="triggerSlotData.currentItem"
              :size="avatarSize"
            >
              <FlotoUserAvatar
                v-if="showAvatar"
                :size="avatarSize"
                :avatar="(triggerSlotData.currentItem || {}).avatar"
                :style="{
                  minWidth: `${
                    avatarSize === 'small' ? 20 : size === 'medium' ? 30 : 20
                  }px`,
                }"
              />
              <div class="text-ellipsis">
                <div class="text-neutral-light">
                  <slot name="label"> </slot>
                </div>
                {{ triggerSlotData.currentItem.name }}
              </div>
            </slot>
            <MIcon name="chevron-down-custom" class="action-icon" size="sm" />
          </span>
        </template>
        <div
          v-else
          @mouseenter="handleMouseEnter"
          @mouseleave="handleMouseLeave"
          @click="triggerSlotData.toggle"
        >
          <MInput
            ref="inputRef"
            read-only
            :value="
              triggerSlotData.currentItem
                ? triggerSlotData.currentItem.text
                : undefined
            "
            :placeholder="placeholder || $t('select_technician')"
            class="dropdown-trigger-input"
            :disabled="disabled"
            :class="[{ 'dropdown-open': triggerSlotData.isOpen }, inputClasses]"
            @focus="handleFocus"
            @keydown.space.prevent.enter.stop="
              ($event) => handleKeyDown($event, 'inputRef')
            "
          >
            <FlotoUserAvatar
              v-if="triggerSlotData.currentItem"
              slot="prefix"
              :size="avatarSize"
              :avatar="(triggerSlotData.currentItem || {}).avatar"
              :style="isRtl ? { left: '5px' } : { right: '5px' }"
            />
            <MIcon
              v-if="isActiveHover && allowClear && !disabled"
              slot="suffix"
              type="fas"
              name="times-circle"
              size="sm"
              class="cursor-pointer"
              @click.stop="handleChange(undefined)"
            />
            <MIcon
              v-else
              slot="suffix"
              type="fal"
              name="chevron-down"
              size="sm"
              class="cursor-pointer text-neutral-light"
            />
          </MInput>
        </div>
      </slot>
    </template>
    <template v-slot:menu-item="{ item, selectItem }">
      <FlotoAnchorLink
        :aria-label="item.name"
        class="flex items-center"
        @click="selectItem(item)"
      >
        <FlotoAvatarName
          :avatar="item.avatar"
          :name="
            item.email || item.userLogOnName
              ? `${item.name} (${item.email || item.userLogOnName})`
              : item.name
          "
          :size="avatarSize"
          :use-icon="[0, 'any'].indexOf(item.id) >= 0"
          show-tooltip
        >
          <template
            v-if="!multiple && !isPortalLogin"
            v-slot:availability-status
          >
            <div class="availability-indicator-wrapper">
              <TechnicianAvailabilityStatus
                :key="item.id"
                :user-id="item.id"
                :logged-in-technicians="loggedInTechnicians"
                class="availability-indicator"
              />
            </div>
          </template>
          <div v-if="!multiple" class="flex technician-indicator-actions">
            <div
              v-if="!multiple && Boolean(item.availabilityStatusId)"
              class="flex items-center"
              :class="{
                'text-secondary-red': item.thresholdViolated,
                'text-secondary-orange': Boolean(item.availabilityStatusId),
              }"
              style="z-index: 2"
            >
              <MTooltip>
                <template v-slot:trigger>
                  <MIcon name="exclamation-triangle" />
                </template>
                {{
                  item.thresholdViolated
                    ? $t('threshold_violated_message', {
                        value: thresholdLeaveBalance,
                      })
                    : $tc('on_leave')
                }}
              </MTooltip>
            </div>
            <div
              v-if="showLoadIndicator"
              class="load-indicator"
              :class="item[`${moduleName}LoadIndicator`]"
            >
              <div></div>
              <div></div>
              <div></div>
            </div>
            <MIcon
              name="check"
              class="text-primary"
              :style="{
                visibility: Array.isArray(value)
                  ? value.indexOf(item.key) >= 0
                  : value === item.key
                  ? 'visible'
                  : 'hidden',
              }"
            />
          </div>
        </FlotoAvatarName>
      </FlotoAnchorLink>
    </template>
    <template v-slot:advanced-search="{ selectItem, resetSearchQuery }">
      <UserSelectionDrawer
        type="technician"
        :group-id="groupId"
        :multiple="multiple"
        :per-page="1000"
        v-bind="$attrs"
        @trigger="resetSearchQuery"
        @submit="
          ($event, items) => handleAddSelectedItem($event, items, selectItem)
        "
      />
    </template>
  </FlotoDropdownPicker>
</template>

<script>
import { TechnicianComputed } from '@state/modules/technician'
import { PreferenceComputed } from '@state/modules/preference'
import {
  getUsersApi,
  getUsersPublicApi,
  getActiveTechniciansApi,
} from '@modules/users/users-api'
import { buildRelationalQualificationStructure } from '../../data/qualification'
import TechnicianAvailabilityStatus from '@components/technician-availability-status'
import UserSelectionDrawer from '@components/item-selection-list/user-selection-drawer'
import { authComputed } from '@state/modules/auth'
import { isLoggedIn } from '@utils/auth'
export default {
  name: 'FlotoTechnicianPicker',
  components: { TechnicianAvailabilityStatus, UserSelectionDrawer },
  inheritAttrs: false,
  model: {
    event: 'change',
  },
  props: {
    moduleName: {
      type: String,
      default() {
        return 'request'
      },
    },
    activeTechnicians: { type: Object, default: undefined },
    hideTenant: { type: Boolean, default: false },
    changeTechnicianOnGroupChange: { type: Boolean, default: false },
    groupId: { type: Number, default: undefined },
    placement: { type: String, default: 'bottomLeft' },
    multiple: { type: Boolean, default: false },
    mandatorySelection: { type: Boolean, default: false },
    keepUnassigned: { type: Boolean, default: false },
    value: { type: [Number, Array, String], default: undefined },
    disabled: { type: Boolean, default: false },
    asInput: { type: Boolean, default: false },
    inputClasses: { type: [String, Object, Array], default: undefined },
    size: {
      type: [String, Number],
      default: 'small',
    },
    placeholder: { type: String, default: undefined },
    excludeIds: {
      type: Array,
      default() {
        return []
      },
    },
    includeIds: {
      type: Array,
      default: undefined,
    },
    validateArchivedValue: { type: Boolean, default: false },
    excludeBlockedTechnicians: { type: Boolean, default: false },
    // eslint-disable-next-line
    showAvatar: { type: Boolean, default: true },
    // eslint-disable-next-line
    allowClear: { type: Boolean, default: true },
    // eslint-disable-next-line
    advancedSearchable: { type: Boolean, default: true },
    focusEventBrodcast: { type: Boolean, default: false },
    showLoadIndicator: { type: Boolean, default: false },
  },
  data() {
    this.selectedEventName = 'technicianSelected'
    return {
      isActiveHover: false,
      isHovering: false,
      archivedOptions: [],
      loggedInTechnicians: {},
    }
  },
  computed: {
    ...PreferenceComputed,
    ...TechnicianComputed,
    ...authComputed,
    avatarSize() {
      if (this.size && /^\d+$/.test(this.size)) {
        return this.size
      }
      if (this.size === 'large') {
        return 30
      }
      return 20
    },
    asInputComputed() {
      if (this.multiple) {
        return true
      }
      return this.asInput
    },
    allOptions() {
      let currentList = this.technicianOptions
      if (this.excludeBlockedTechnicians) {
        currentList = currentList.filter(
          (c) =>
            (c.blocked !== true && c.isSuperAdmin === false) ||
            (c.isSuperAdmin !== true && c.blocked === false)
        )
        return currentList
      }
      return currentList.concat(this.archivedOptions)
    },
    options() {
      let currentList = this.allOptions
      if (
        this.mandatorySelection ||
        (this.multiple && this.keepUnassigned === false)
      ) {
        currentList = currentList.map((i) => ({
          ...i,
          ...(i.id === 0 ? { disabled: true } : {}),
        }))
      }
      if (this.groupId) {
        currentList = currentList.filter(
          (u) => (u.groups || []).indexOf(this.groupId) >= 0 || u.id === 0
        )
      }
      if (this.hideTenant) {
        currentList = currentList.filter((u) => u.isTenant !== true)
      }
      if (this.excludeIds.length) {
        currentList = currentList.filter(
          (u) => this.excludeIds.indexOf(u.id) === -1
        )
      }
      if (this.includeIds) {
        const includeIds = this.includeIds
        currentList = currentList.filter((u) => includeIds.indexOf(u.id) >= 0)
      }
      return currentList
    },
    currentUser() {
      if (this.value) {
        return (this.allOptions || []).find(
          (p) => p.key === this.value || p.key === parseInt(this.value)
        )
      }
      return undefined
    },
  },
  watch: {
    groupId(newValue) {
      if (!this.changeTechnicianOnGroupChange) {
        return
      }
      if (this.value === 0) {
        return
      }
      if (newValue === 0) {
        return
      }
      if (
        this.currentUser &&
        (this.currentUser.groups || []).indexOf(this.groupId) === -1
      ) {
        this.handleChange(0)
      }
    },
    activeTechnicians: {
      immediate: true,
      handler(newValue) {
        if (newValue && !this.isPortalLogin && !this.multiple) {
          this.loggedInTechnicians = newValue
        }
      },
    },
  },
  created() {
    if (!this.multiple && !this.isPortalLogin && !this.disabled) {
      if (!this.activeTechnicians) {
        this.getActiveTechnicians()
      } else {
        this.loggedInTechnicians = this.activeTechnicians
      }
    }
    if (this.value) {
      const v = Array.isArray(this.value) ? this.value : [this.value]
      const allAvailableUserIds = this.allOptions.map((u) => u.key)
      const missingUsers = v.filter(
        (userId) => allAvailableUserIds.indexOf(userId) === -1
      )
      if (missingUsers.length) {
        this.fetchMissingTechnicians(missingUsers)
      }
    }
    // set dynamic watch on value for handle archived value validation
    if (this.validateArchivedValue && !this.multiple) {
      this.$watch('value', {
        immediate: true,
        handler(newValue) {
          if (
            newValue &&
            (this.allOptions.filter((o) => o.key === newValue).length === 0 ||
              this.allOptions.find((o) => o.key === newValue).archived)
          ) {
            this.$nextTick(() => {
              this.handleChange(0)
            })
          }
        },
      })
    }
  },
  methods: {
    handleKeyDown($event, ref) {
      const inputRef = this.$refs[ref]
      if ($event instanceof KeyboardEvent) {
        if (inputRef) {
          if (inputRef instanceof HTMLElement) {
            inputRef.click()
          } else {
            inputRef.$el.click()
          }
        }
      }
    },
    handleSelected(data) {
      this.$emit(this.selectedEventName, data)
    },
    fetchMissingTechnicians(technicianIds) {
      const search = {
        quals: [
          buildRelationalQualificationStructure(
            'id',
            'in',
            technicianIds,
            'long',
            'db'
          ),
        ],
      }
      let getFunction
      if (isLoggedIn()) {
        getFunction = getUsersApi
      } else {
        getFunction = getUsersPublicApi
      }
      return getFunction('requester', {}, search, undefined, undefined, {
        archived: true,
      }).then((data) => {
        this.archivedOptions = Object.freeze(
          (data.items || []).map((u) => ({
            key: u.id,
            id: u.id,
            text: u.text,
            userLogOnName: u.userLogOnName,
            name: u.name,
            email: u.email,
            avatar: u.avatar,
            requestloadIndicator: u.loadIndicator || 'low',
            problemLoadIndicator: u.problemLoadIndicator || 'low',
            changeLoadIndicator: u.changeLoadIndicator || 'low',
            releaseLoadIndicator: u.releaseLoadIndicator || 'low',
            archived: u.archived, //! this.multiple,
            disabled: u.archived,
          }))
        )
      })
    },
    handleAddSelectedItem(value, items, selectItem) {
      this.$nextTick(() => {
        if (this.multiple) {
          this.handleChange(value)
        } else {
          this.handleChange(value)
        }
      })
    },
    handleChange(option) {
      if (this.multiple) {
        this.$emit(
          'selected',
          option.map((id) =>
            this.options.find(
              (o) => o.key === id || o.id === id || o.value === id
            )
          )
        )
        this.$emit('change', option)
        this.$emit('blur')
      } else {
        this.$emit(
          'selected',
          this.options.find(
            (o) => o.key === option || o.id === option || o.value === option
          )
        )
        this.$emit('change', option || 0)
        this.$emit('blur')
      }
    },
    getActiveTechnicians() {
      getActiveTechniciansApi().then((data) => {
        this.loggedInTechnicians = data
      })
    },
    handleMouseEnter() {
      this.isHovering = true
      if (this.value) {
        this.isActiveHover = true
      }
    },
    handleMouseLeave() {
      this.isActiveHover = false
      this.isHovering = false
    },
    handleFocus(e) {
      if (this.focusEventBrodcast && !this.isHovering) {
        e.target.click()
      }
    },
  },
}
</script>

<style lang="less">
.load-indicator {
  align-self: normal;
  width: 8px;

  @apply flex flex-col;

  div {
    margin-bottom: 1px;

    @apply flex-1;

    &:first-child {
      border-top-left-radius: 4px;
      border-top-right-radius: 4px;
    }

    &:last-child {
      margin-bottom: 0;
      border-bottom-right-radius: 4px;
      border-bottom-left-radius: 4px;
    }
  }

  &.low {
    div {
      border: 1px solid @secondary-green;

      &:last-child {
        background: @secondary-green;
      }
    }
  }

  &.medium {
    // stylelint-disable-next-line
    div {
      background: @secondary-yellow;
      border: 1px solid @secondary-yellow;

      &:first-child {
        background: transparent;
      }
    }
  }

  &.high {
    // stylelint-disable-next-line
    div {
      background: @secondary-red;
      border: 1px solid @secondary-red;
    }
  }
}

.availability-indicator-wrapper {
  position: absolute;
  top: -3px;
  left: 23px;
  z-index: 1;

  .availability-indicator {
    & .dot {
      width: 8px !important;
      min-width: 8px !important;
      height: 8px !important;
      min-height: 8px !important;
    }
  }
}

.technician-indicator-actions {
  @apply flex items-center;

  gap: 0.5rem;
}
</style>
